import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, BookOpen, FileText } from 'lucide-react';
import axios from 'axios';
import ModernCard from '../components/ModernCard';
import { API_ENDPOINTS } from '../config/api';
import { createSlug } from '../utils/urlHelpers';
import './FixedUnitPage.css';

const FixedUnitPage = () => {
  const { unitName } = useParams();
  // The unitName from URL is a slug, we need to find the original unit name
  const [originalUnitName, setOriginalUnitName] = useState('');
  const [unitData, setUnitData] = useState({ unit: '', files: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // First get all units to find the original name
    const findOriginalUnitName = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.UNITS);
        const units = response.data;
        const originalName = units.find(unit => {
          const slug = unit.toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
          return slug === unitName;
        });
        
        if (originalName) {
          setOriginalUnitName(originalName);
          fetchUnitFiles(originalName);
        } else {
          setError('Unit not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error finding unit name:', err);
        setError('Failed to load unit. Please try again.');
        setLoading(false);
      }
    };

    findOriginalUnitName();
  }, [unitName]);

  const fetchUnitFiles = async (unitName) => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.UNIT_FILES(unitName));
      setUnitData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching unit files:', err);
      setError('Failed to load unit files. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const notesFiles = unitData.files.filter(file => file.type === 'notes');
  const pastPaperFiles = unitData.files.filter(file => file.type === 'past-paper');

  if (loading) {
    return (
      <div className="fixed-unit-page">
        <div className="page-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading unit files...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed-unit-page">
        <div className="page-container">
          <div className="error-state">
            <h3>Error Loading Files</h3>
            <p>{error}</p>
            <button className="retry-button" onClick={fetchUnitFiles}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed-unit-page">
      <div className="page-container">
        {/* Header */}
        <header className="page-header">
          <Link to="/" className="back-button">
            <ArrowLeft size={20} />
            <span>Back to Units</span>
          </Link>
          <h1 className="page-title">{originalUnitName}</h1>
          <p className="page-subtitle">Computer Science Year 1 - Semester 2</p>
        </header>

        {/* Category Selection */}
        <div className="category-selection">
          <h2 className="selection-title">Choose what you want to view:</h2>
          <div className="category-buttons">
            <Link to={`/unit/${unitName}/notes`} className="category-button">
              <ModernCard
                icon={BookOpen}
                title="Notes"
                description={`View ${notesFiles.length} lecture notes and study materials`}
                buttonText={`View Notes (${notesFiles.length})`}
                variant="category"
                className="category-card notes-card"
              />
            </Link>
            
            <Link to={`/unit/${unitName}/past-papers`} className="category-button">
              <ModernCard
                icon={FileText}
                title="Past Papers"
                description={`View ${pastPaperFiles.length} past examination papers`}
                buttonText={`View Past Papers (${pastPaperFiles.length})`}
                variant="category"
                className="category-card papers-card"
              />
            </Link>
          </div>
        </div>

        {/* Empty State */}
        {unitData.files.length === 0 && (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No Files Found</h3>
            <p>No files are available for this unit.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FixedUnitPage;
